// src/shell/IOSInstallPrompt.jsx
// Shows a one-time "Add to Home Screen" prompt for iOS Safari users.
// iOS Safari doesn't support beforeinstallprompt — must guide manually.
// Only shows if: iOS + Safari + not already installed as PWA + not dismissed.

import { useState, useEffect } from 'react';
import { T } from '@components/theme';

const DISMISSED_KEY = 'eos-ios-install-dismissed';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isSafari() {
  return /safari/i.test(navigator.userAgent) && !/chrome|crios|fxios/i.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true;
}

export function IOSInstallPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Only show on iOS Safari, not installed, not previously dismissed
    if (!isIOS() || !isSafari() || isStandalone()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;
    // Delay slightly so it doesn't appear on first paint
    const t = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1');
    setShow(false);
  }

  return (
    <div style={{
      position: 'fixed', bottom: 80, left: 12, right: 12, zIndex: 2000,
      background: '#1c1917', border: `1px solid ${T.amber}`,
      borderRadius: 14, padding: '14px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      fontFamily: 'system-ui',
    }}>
      {/* Arrow pointing down toward bottom bar */}
      <div style={{
        position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: `8px solid ${T.amber}`,
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 28, flexShrink: 0 }}>📲</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 13, color: T.text, marginBottom: 4 }}>
            Install EyeOnStorm
          </div>
          <div style={{ fontSize: 11, color: '#a8a29e', lineHeight: 1.5, marginBottom: 10 }}>
            Add to your Home Screen for offline access during emergencies — works without internet.
          </div>
          <div style={{ fontSize: 11, color: '#d6d3d1', lineHeight: 1.6 }}>
            <div>1. Tap <span style={{ color: T.amber, fontWeight: 700 }}>Share</span> <span style={{ fontSize: 14 }}>⎙</span> at the bottom of Safari</div>
            <div>2. Tap <span style={{ color: T.amber, fontWeight: 700 }}>Add to Home Screen</span></div>
            <div>3. Tap <span style={{ color: T.amber, fontWeight: 700 }}>Add</span></div>
          </div>
        </div>
        <button onClick={dismiss} style={{
          background: 'none', border: 'none', color: '#78716c',
          fontSize: 18, cursor: 'pointer', flexShrink: 0,
          padding: '0 4px', lineHeight: 1,
        }}>✕</button>
      </div>

      <button onClick={dismiss} style={{
        marginTop: 12, width: '100%', padding: '8px',
        background: 'rgba(217,119,6,0.12)', border: `1px solid ${T.amber}44`,
        borderRadius: 8, color: T.amber, fontSize: 11, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'system-ui',
      }}>
        Maybe later
      </button>
    </div>
  );
}
